'use client';

import { AwardBadge } from '@/app/components/shared/Activity/Award';
import { HoverContent } from '@/app/components/shared/Community/components/HoverContent';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import { TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import type { ActivityInteractionParams, WorkWithTask } from '@/app/types';
import { CommunityEnum } from '@/app/types';
import { getAspectRatio, mapTypeToExploreTab, reportEvent } from '@/app/utils';
import { generateCommunityShareUrl } from '@/app/utils/client';
import { Link, usePathname } from '@/i18n/routing';

interface ActivityCardProps {
  item: WorkWithTask;
  onVote: (params: Omit<ActivityInteractionParams, 'obj_type'>) => void;
  onUnVote: (params: Omit<ActivityInteractionParams, 'obj_type'>) => void;
  interactionPosition: string;
  activityId: string;
  workTab: string;
  position: number;
}

export const ActivityCard = ({
  item,
  onVote,
  onUnVote,
  interactionPosition,
  activityId,
  workTab,
  position,
}: ActivityCardProps) => {
  const work = item.work;
  const type = mapTypeToExploreTab(work?.type);
  const currentActivityId = activityId || work?.activity_id || '';
  const pathname = usePathname();

  const handleVote = ({ id }: { id: string }) => {
    onVote({ activityId: currentActivityId, id, voteType: 'like' });
  };

  const handleUnVote = ({ id }: { id: string }) => {
    onUnVote({ activityId: currentActivityId, id, voteType: 'dislike' });
  };

  const handleWorkClick = () => {
    reportEvent('work_item_click', {
      activity_id: currentActivityId,
      work_id: work?.id || '',
      work_author_id: work?.creator?.id || '',
      work_tab: workTab,
      position,
    });
  };

  const renderImageContent = () => (
    <div
      className="relative h-full w-full overflow-hidden rounded-4"
      style={{ aspectRatio: getAspectRatio({ size: work?.creation?.size }) }}
    >
      <MediaWithMaskBg src={work?.creation?.cover_uri || work?.creation?.uri || ''} />
    </div>
  );

  const renderVideoContent = () => (
    <HoverPlayMedia
      id={work?.id || ''}
      uri={work?.creation?.uri}
      coverUri={work?.creation?.cover_uri}
      borderRadiusClassName="rounded-4"
      showMaskBg={true}
      aspectRatio={getAspectRatio({ size: work?.creation?.size })}
      onPlayStart={() => {
        reportEvent('inspiration_interaction', {
          interaction_id: work?.id || '',
          interaction_type: 'play',
          interaction_position: interactionPosition || pathname,
          content_type: 'video',
        });
      }}
      eventName={TRACE_EVENT_NAME[type]}
      eventProperties={{
        [TRACE_EVENT_ID[type]]: work?.id || '',
        interaction_position: interactionPosition || pathname,
      }}
    />
  );

  return (
    <div className="group relative">
      <Link
        prefetch={false}
        key={work?.id}
        href={generateCommunityShareUrl({ id: work?.id || '', withOrigin: false, isActivity: true })}
        onClick={handleWorkClick}
      >
        {type === CommunityEnum.ImageAsset ? renderImageContent() : renderVideoContent()}
      </Link>

      {/* 奖项展示 */}
      <AwardBadge award={work?.award} className="absolute top-3 left-3 z-[11] flex flex-col items-start gap-1" />

      {/* 悬浮交互层 */}
      <HoverContent currentItem={work} handleVote={handleVote} handleUnVote={handleUnVote} />
    </div>
  );
};
